import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const plgItems = [
  {
    title: "Onboarding optimization",
    description:
      "Reduce time-to-value so new users experience the product's core benefit faster. Every day of confusion is a lost customer.",
  },
  {
    title: "Activation funnels",
    description:
      "Identify and remove the friction between sign-up and the moment users realize the product is indispensable.",
  },
  {
    title: "Expansion revenue",
    description:
      "Build natural upgrade paths into the product experience so growth comes from usage, not just sales conversations.",
  },
  {
    title: "Reduced sales dependency",
    description:
      "Let the product qualify and convert users on its own. Sales becomes a closer, not a convincer.",
  },
];

const plgMetrics = [
  {
    metric: "Activation Rate",
    description:
      "The percentage of new users who reach the moment they experience core product value. This is the single most important PLG metric. If users don't activate, nothing else matters.",
  },
  {
    metric: "Time-to-Value",
    description:
      "How quickly a new user goes from sign-up to their first meaningful outcome. Shorter time-to-value means higher activation, lower churn, and faster revenue.",
  },
  {
    metric: "Expansion Revenue",
    description:
      "Revenue generated from existing customers through upgrades, add-ons, and increased usage. In PLG, expansion revenue should grow faster than new customer revenue.",
  },
  {
    metric: "Net Revenue Retention",
    description:
      "The percentage of revenue retained from existing customers, including expansion and contraction. Best-in-class PLG companies exceed 120% NRR.",
  },
  {
    metric: "Product-Qualified Leads",
    description:
      "Users who demonstrate buying intent through product behavior, not form fills. PQLs convert at 5-10x the rate of marketing-qualified leads because they already know the product works.",
  },
  {
    metric: "Self-Serve Conversion",
    description:
      "The percentage of users who convert to paid without human intervention. Higher self-serve conversion means lower CAC and better unit economics at scale.",
  },
];

const plgMistakes = [
  {
    title: "Treating PLG as a marketing strategy",
    description:
      "PLG is a company-wide operating model, not a top-of-funnel tactic. Adding a free tier and calling it PLG misses the point. Real PLG requires product, engineering, sales, and success all aligned around product-driven growth.",
  },
  {
    title: "No product leadership driving it",
    description:
      "PLG without senior product leadership is a recipe for wasted cycles. Someone needs to own activation metrics, design the upgrade paths, build the experimentation muscle, and connect product work to revenue. Without that, PLG stays a buzzword.",
  },
  {
    title: "Measuring vanity metrics",
    description:
      "Sign-ups, page views, and MAU look good in dashboards but don't predict revenue. PLG requires measuring activation, time-to-value, expansion revenue, and self-serve conversion. If you can't tie a product metric to dollars, it's vanity.",
  },
  {
    title: "Ignoring enterprise needs",
    description:
      "PLG doesn't mean no sales team. Enterprise customers need procurement support, security reviews, and custom integrations. The best PLG companies use product to qualify and warm leads, then hand them to sales for enterprise conversion. Product-led sales, not product-only sales.",
  },
];

const faqs = [
  {
    question: "What is product-led growth?",
    answer:
      "PLG is a business methodology where the product itself drives customer acquisition, activation, and expansion. Instead of relying solely on sales, the product delivers value that converts users. Companies like Slack, Dropbox, and Figma popularized the model, but it applies to any technology company where the product can demonstrate value before a purchase decision.",
  },
  {
    question: "Can established companies adopt product-led growth?",
    answer:
      "Yes. PLG isn't just for startups. Established technology companies, including PE-backed companies, use PLG to improve unit economics and accelerate revenue without proportionally growing the sales team. The transition requires experienced product leadership to redesign the user journey, build self-serve conversion paths, and restructure teams around product-led metrics.",
  },
  {
    question: "How does FractionalCPO drive PLG?",
    answer:
      "FractionalCPO embeds senior product leadership that has done it before. We restructure teams, optimize funnels, and build experimentation systems that compound growth. Every engagement starts with identifying the highest-leverage opportunities in your product funnel, then executing against them with measurable results.",
  },
  {
    question: "What are the key PLG metrics?",
    answer:
      "The six metrics that matter most for PLG are activation rate, time-to-value, expansion revenue, net revenue retention, product-qualified leads, and self-serve conversion rate. Together, these tell you whether your product is actually driving growth or just generating activity. We help companies instrument, measure, and improve all six.",
  },
  {
    question: "What's the difference between PLG and sales-led growth?",
    answer:
      "In sales-led growth, the sales team drives acquisition, qualification, and conversion. In PLG, the product does the heavy lifting for acquisition and qualification, and sales focuses on enterprise conversion and expansion. Most successful companies use a hybrid model where PLG generates product-qualified leads and sales closes larger accounts. The result is better unit economics and faster growth.",
  },
  {
    question: "How does FractionalCPO approach PLG differently?",
    answer:
      "We don't deliver a PLG playbook and walk away. We embed senior product leaders who take ownership of PLG metrics and execution. We restructure teams around activation funnels, build experimentation engines, and connect every product decision to revenue impact. We have driven doubled conversion rates and record growth across PE-backed and growth-stage companies.",
  },
];

const ProductLedGrowth = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="Product-Led Growth for Technology Companies"
        description="Drive product-led growth at scale with senior product leadership. FractionalCPO brings the experience to build PLG systems that accelerate revenue and improve unit economics."
        path="/product-led-growth"
      />

      {/* Hero */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium"
          >
            Growth Strategy
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(2.5rem,8vw,6rem)] font-light leading-[0.95] tracking-[-0.03em] mb-12"
          >
            Product-Led Growth for{" "}
            <span className="text-[#002178] font-medium">
              Technology Companies
            </span>
          </motion.h1>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl"
          >
            <p className="text-xl md:text-2xl leading-relaxed font-medium text-[#1a1a1a] mb-6">
              PLG is not just for startups.
            </p>
            <p className="text-lg text-black/60 leading-relaxed mb-8">
              PE-backed technology companies use product-led growth to accelerate
              revenue and improve unit economics. The product becomes the primary
              engine for acquisition, activation, and expansion. But it only
              works with senior product leadership who have shipped PLG at scale
              before.
            </p>
            <button
              onClick={() => { trackEvent('contact_click', { source: 'hero' }); setIsContactOpen(true); }}
              className="inline-block px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform"
            >
              Let's chat
            </button>
          </motion.div>
        </div>
      </section>

      {/* What PLG looks like at scale */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            What PLG looks like{" "}
            <span className="text-[#002178] font-medium">at scale</span>
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-16">
            {plgItems.map((item, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="border-l-2 border-[#002178]/20 pl-6"
              >
                <h3 className="text-xl font-medium text-[#1a1a1a] mb-3">
                  {item.title}
                </h3>
                <p className="text-base text-black/60 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Retention callout */}
          <motion.div
            {...fadeUp}
            className="border-l-4 border-[#002178] bg-white p-8 md:p-10"
          >
            <h3 className="text-2xl md:text-3xl font-medium text-[#1a1a1a] mb-4">
              Retention is an output, not an input
            </h3>
            <p className="text-lg text-black/60 leading-relaxed">
              Most teams try to fix retention directly. That rarely works. Retention improves when you fix what comes before it: acquisition quality and activation experience. Get the right users in and deliver value fast. Retention follows. We start from the top of the funnel and work down.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Where PLG fails */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <motion.h2
              {...fadeUp}
              className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
            >
              Where PLG <span className="font-medium">fails</span>
            </motion.h2>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-white/70 leading-relaxed mb-6"
            >
              Without senior product leadership, product-led growth becomes a
              buzzword. Teams adopt the language without the discipline. They
              launch a free tier, call it PLG, and wonder why nothing changed.
            </motion.p>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-white/70 leading-relaxed"
            >
              Most PLG initiatives fail because they lack experienced leadership
              who have shipped PLG at scale before. Someone who knows how to
              restructure teams around product-led metrics, build
              experimentation systems, and connect product decisions to revenue
              outcomes. That is the gap FractionalCPO fills.
            </motion.p>
          </div>
        </div>
      </section>

      {/* PLG Metrics That Matter */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <motion.p
                {...fadeUp}
                className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
              >
                Measurement
              </motion.p>
              <motion.h2
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                PLG metrics{" "}
                <span className="text-[#002178] font-medium">that matter</span>
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-black/60 leading-relaxed"
              >
                Most companies measure the wrong things. Sign-ups and page views
                look good in dashboards but don't predict revenue. These are the
                six metrics that actually tell you whether PLG is working.
              </motion.p>
            </div>

            <div className="space-y-0">
              {plgMetrics.map((item, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="group py-8 border-b border-black/10"
                >
                  <div className="flex gap-6 items-baseline">
                    <span className="text-[#002178]/30 font-mono text-sm">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="text-xl md:text-2xl font-medium mb-2 group-hover:text-[#002178] transition-colors">
                        {item.metric}
                      </h3>
                      <p className="text-black/50 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common PLG Mistakes */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
          >
            Pitfalls
          </motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            Common PLG <span className="font-medium">mistakes</span>
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {plgMistakes.map((item, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="border-l-2 border-[#002178]/20 pl-6"
              >
                <h3 className="text-xl font-medium text-[#1a1a1a] mb-3">
                  {item.title}
                </h3>
                <p className="text-base text-black/60 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How we drive PLG */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <motion.h2
              {...fadeUp}
              className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
            >
              How we drive{" "}
              <span className="text-[#002178] font-medium">
                product-led growth
              </span>
            </motion.h2>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-black/60 leading-relaxed mb-6"
            >
              A decade of embedded product leadership across technology
              companies. We have driven doubled conversion rates, record growth,
              and sustainable PLG systems that compound over time.
            </motion.p>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-black/60 leading-relaxed mb-8"
            >
              Every engagement starts with a{" "}
              <Link
                to="/assessment"
                className="text-[#002178] underline underline-offset-4 hover:text-[#002178]/80"
              >
                product assessment
              </Link>{" "}
              to find the highest-leverage opportunities. Then we execute. You
              can see what that looks like in our{" "}
              <Link
                to="/case-study"
                className="text-[#002178] underline underline-offset-4 hover:text-[#002178]/80"
              >
                case study
              </Link>
              .
            </motion.p>

            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.3 }}>
              <button
                onClick={() => { trackEvent('contact_click', { source: 'how_we_drive_plg' }); setIsContactOpen(true); }}
                className="inline-block px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform"
              >
                Let's chat
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <div className="bg-[#F5F5F0]">
        <FAQSchema faqs={faqs} heading="Frequently Asked Questions" />
      </div>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default ProductLedGrowth;
